import { Country } from "../Country.js";
import type { CountrySettings } from "../CountrySettings.js";
import type { Currency } from "../Currency.js";
import type { Customer } from "../Customer.js";
import { DeliveryType } from "../DeliveryType.js";
import { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { convertPriceInCzkToCurrency } from "./convertPriceInCzkToCurrency.js";
import { getCustomerType } from "./getCustomerType.js";

interface DeliveryPriceSettings {
	applyTax: boolean;
	countrySettings: CountrySettings[];
	currency: Currency;
	customer: Customer | null;
	exchangeRates: ExchangeRate[];
	vatType: EconomicSubjectVatType;
}

export function calculatePriceOfDelivery(
	destinationCountry: Country | "outsideEu",
	deliveryType: DeliveryType,
	{ countrySettings, customer, currency, exchangeRates, applyTax, vatType }: DeliveryPriceSettings,
): TaxedPrice {
	const delivery = countrySettings.find((setting) => setting.country === destinationCountry)?.data?.delivery;
	if (!delivery) {
		throw new Error(`Delivery is not enabled for country: ${destinationCountry}`);
	}

	let { shippingVatRate } = delivery;
	if (vatType === EconomicSubjectVatType.CzSubject && destinationCountry !== Country.Czechia) {
		const czDelivery = countrySettings.find((setting) => setting.country === Country.Czechia)?.data?.delivery;
		if (czDelivery) {
			({ shippingVatRate } = czDelivery);
		}
	}
	const taxRate = applyTax ? shippingVatRate : 0;

	if (deliveryType === DeliveryType.PersonalPickup) {
		return { taxRate, withTax: 0, withoutTax: 0 };
	}

	const type = getCustomerType(customer);
	const price = {
		B2c: delivery.flatPriceForB2c,
		B2b: delivery.flatPriceForB2b,
		B2f: delivery.flatPriceForB2b,
	}[type];

	const withoutTax = convertPriceInCzkToCurrency(price, currency, exchangeRates);
	const withTax = calculatePriceWithTax(withoutTax, taxRate);
	return { taxRate, withTax, withoutTax };
}
